import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, RefreshCw, Filter, X, FilterX, Tag, ChevronDown, 
  Calendar, Database, User, PackagePlus, Package, Building2, ListPlus, List,
  DollarSign, Download, ChevronRight, Boxes, Banknote
} from 'lucide-react';
import FacturacionMenuHeader from '../components/FacturacionMenuHeader';
import FactLoader from './FactLoader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import './DashboardBultos.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dimassiLogo from '../assets/Dimassi_Logo_Negro.png';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const PAGE_SIZE = 1000;

const filterLabels = {
  FACT_PROVEEDOR: "Proveedor",
  FACT_MARCA: "Marca",
  FACT_LINEA: "Línea",
  FACT_PORTAFOLIO: "Portafolio",
  FACT_CLIENTE_PRINCIPAL: "Cliente Principal",
  FACT_VENDEDOR_REGISTRO: "Vendedor Registro",
  FACT_VENDEDOR_ACTUAL: "Vendedor Actual",
  FACT_CANAL: "Canal",
  FACT_REGION: "Región",
  FACT_STATUS_ANULADA: "Anulada",
  FACT_PRODUCTO: "Producto",
  FACT_CLIENTE_NOMBRE: "Cliente Nombre",
  FACT_ART_ID: "ID Artículo",
  FACT_ADMIN: "Admin Registro",
  FACT_UND: "Unidad",
  FACT_IMP: "Impuesto",
  NCD_DEVOLUCION: "Devolución",
  NCD_OBSERVACION: "Obs. Devolución",
  NCF_NRO_DOC: "Nota Financiera",
  NCF_CONCEPTO_NOTA: "Concepto Nota",
  FACT_MONEDA: "Moneda",
  FACT_SEG_DES: "Segmento",
  FACT_FACTURA: "Factura"
};

const INITIAL_FILTERS = {
  FACT_PROVEEDOR: [], FACT_MARCA: [], FACT_LINEA: [], FACT_PORTAFOLIO: [],
  FACT_CLIENTE_PRINCIPAL: [], FACT_VENDEDOR_REGISTRO: [], FACT_VENDEDOR_ACTUAL: [],
  FACT_CANAL: [], FACT_REGION: [], FACT_STATUS_ANULADA: [], FACT_PRODUCTO: [], 
  FACT_CLIENTE_NOMBRE: [], FACT_ADMIN: [], FACT_UND: [], FACT_IMP: [],
  NCD_DEVOLUCION: [], NCD_OBSERVACION: [], NCF_NRO_DOC: [], NCF_CONCEPTO_NOTA: [], FACT_MONEDA: [], FACT_SEG_DES: [], FACT_FACTURA: []
};

const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);
const formatBs = (num) => new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'VES' }).format(num || 0);
const formatNum = (num) => new Intl.NumberFormat('de-DE').format(num || 0);

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => String(opt).toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 100);
  
  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button className={`filter-pill-button ${selectedValues.length > 0 ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{selectedValues.length > 0 ? (selectedValues.length === 1 ? selectedValues[0] : `${selectedValues.length} Selec.`) : 'Todos'}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className="filter-dropdown-panel">
          <div className="search-wrapper">
            <Search size={14} className="search-icon" />
            <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
          </div>
          <div className="options-list">
            {filteredOptions.map((opt, i) => (
              <label key={i} className="checkbox-row">
                <input type="checkbox" checked={selectedValues.includes(opt)} onChange={() => {
                  const newVals = selectedValues.includes(opt) ? selectedValues.filter(v => v !== opt) : [...selectedValues, opt];
                  onChange(name, newVals);
                }} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
          {selectedValues.length > 0 && <button className="reset-pill-link" onClick={() => onChange(name, [])}>Limpiar</button>}
        </div>
      )}
    </div>
  );
};

const DetalleFact = () => {
  const { factQuery, getFactFilters, loading, error } = useQuery();
  const { session } = useAuth();
  const [isQuerying, setIsQuerying] = useState(false);
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const filtersInitialized = useRef(false);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [dynamicYears, setDynamicYears] = useState([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [globalTotals, setGlobalTotals] = useState({ bultos: 0, usd: 0 });

  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [expandedRows, setExpandedRows] = useState({});

  const searchRef = useRef(null);
  const tableContainerRef = useRef(null);


  const today = useMemo(() => new Date(), []);
  const nombreMesActual = useMemo(() => today.toLocaleString('es-ES', { month: 'long' }).toUpperCase(), [today]);

  const [selectedYear, setSelectedYear] = useState([today.getFullYear()]);
  const [selectedMonth, setSelectedMonth] = useState([nombreMesActual]);
  // Cambiamos a [] por defecto para ver todo el mes al entrar
  const [selectedDay, setSelectedDay] = useState([]); 

  const meses = useMemo(() => ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"], []);
  const days = useMemo(() => Array.from({length: 31}, (_, i) => i + 1), []);


  const pageTotalUSD = useMemo(() => {
    return rows.reduce((acc, row) => acc + (row.FACT_MONTO_USD || 0), 0);
  }, [rows]);


  const groupedRows = useMemo(() => {
  const grouped = {};

  rows.forEach((row) => {
    const factura = row.FACT_FACTURA || 'SIN_FACTURA';

    if (!grouped[factura]) {
      grouped[factura] = {
        factura,
        clienteId: row.FACT_CLIENTE_ID,
        cliente: row.FACT_CLIENTE_NOMBRE,
        moneda: row.FACT_MONEDA,
        fecha: row.display_date || row.FACT_FECHA,

        totalCantidad: 0,
        totalUSD: 0,
        totalBs: 0,
        totalCUND: 0,
        descuentoPct: 0,
        descuentoMontoBs: 0,
        descuentoMontoUSD: 0,
        netoFinalBs: 0,
        netoFinalUSD: 0,

        items: []
      };
    }

    grouped[factura].items.push(row);

    grouped[factura].totalCantidad += Number(row.FACT_CANTIDAD || 0);
    grouped[factura].totalCUND += Number(row.FACT_UNIDAD || 0);
    const descuentoPct = Number(row.FACT_DESC || 0);

    const netoBs = Number(row.FACT_NETO || 0);
    const netoUSD = Number(row.FACT_MONTO_USD || 0);

    const descuentoBs = netoBs * (descuentoPct / 100);
    const descuentoUSD = netoUSD * (descuentoPct / 100);

    const brutoBs = netoBs + descuentoBs;
    const brutoUSD = netoUSD + descuentoUSD;

    grouped[factura].descuentoMontoBs += descuentoBs;
    grouped[factura].descuentoMontoUSD += descuentoUSD;

    grouped[factura].netoFinalBs += netoBs;
    grouped[factura].netoFinalUSD += netoUSD;

    grouped[factura].totalBs += brutoBs;
    grouped[factura].totalUSD += brutoUSD;

  });

  Object.values(grouped).forEach(group => {
    group.descuentoPct =
      group.totalBs > 0
        ? (group.descuentoMontoBs / group.totalBs) * 100
        : 0;
  });

  return Object.values(grouped);
}, [rows]);

const toggleFactura = (factura) => {
  setExpandedRows(prev => ({
    ...prev,
    [factura]: !prev[factura]
  }));
};

  // Corregido: Función highlightMatch para resaltar texto
  const highlightMatch = (text, query) => {
    if (!query || typeof text !== 'string') return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, i) => 
          part.toLowerCase() === query.toLowerCase() ? (
            <strong key={i} style={{ color: '#00c6ff' }}>{part}</strong>
          ) : (
            part
          )
        )}
      </>
    );
  };

  useEffect(() => {
    const handleClick = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (searchValue.length < 2) { setSuggestions([]); return; }
    const term = searchValue.toLowerCase();
    const results = [];
    const categories = [
      { key: 'FACT_PRODUCTO', label: 'Producto', icon: <Package size={14}/> },
      { key: 'FACT_CLIENTE_NOMBRE', label: 'Cliente', icon: <User size={14}/> },
      { key: 'FACT_MARCA', label: 'Marca', icon: <Tag size={14}/> },
      { key: 'FACT_CLIENTE_PRINCIPAL', label: 'Grupo', icon: <Building2 size={14}/> }
    ];
    categories.forEach(cat => {
      const opts = availableOptions[cat.key] || config?.availableOptions?.[cat.key] || [];
      const matches = opts.filter(opt => String(opt).toLowerCase().includes(term)).slice(0, 5)
        .map(val => ({ key: cat.key, value: val, category: cat.key, catLabel: cat.label, icon: cat.icon }));
      results.push(...matches);
    });
    setSuggestions(results);
    setShowSuggestions(true);
  }, [searchValue, availableOptions, config]);

  const buildQueryParams = useCallback((pageNum = 1) => ({
    page_anios: selectedYear.map(Number),
    page_meses: selectedMonth,
    page_dias: selectedDay.map(Number),
    filters,
    page: pageNum,
    page_size: PAGE_SIZE,
    user_info: session?.user
  }), [selectedYear, selectedMonth, selectedDay, filters, session]);

    const executeQuery = useCallback(async (params) => {
    setIsQuerying(true);
    try {
      const result = await factQuery(params);
      
      // ✅ Extracción robusta de datos: Soporta arrays directos o múltiples nombres de tabla (Parquet)
      const tableData =
      result?.data?.rows ||
      result?.data ||
      result?.table_fact?.rows ||
      result?.table_fact ||
      [];

    const totals =
      result?.totals?.rows?.[0] ||
      result?.totals?.[0] ||
      {};

    console.log("PARSED DATA", tableData);
    console.log("PARSED TOTALS", totals);

    if (Array.isArray(tableData)) {
      setRows(tableData);

      setTotalCount(
        Number(
          totals.total_count ||
          totals.total_records ||
          tableData.length
        )
      );

      setGlobalTotals({
        bultos: Number(totals.bultos || 0),
        usd: Number(totals.usd || 0),
        total_facturas: Number(totals.total_facturas || 0),
        unidad: Number(totals.unidad || 0),
        bs: Number(totals.bs || 0)
      });


      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    }
    } catch (e) {
      console.error('Query error:', e);
    } finally {
      setIsQuerying(false); // <-- Apagar al terminar (aunque falle)
    }
  }, [factQuery])

  const handleApplyFilters = useCallback((page = 1) => {
    setCurrentPage(page);
    setShowSuggestions(false);
  }, []);

  // Load config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const conf = await getFactFilters();
        if (conf) {
          setConfig(conf);
          setDynamicYears(conf.years || []);
          const options = conf.availableOptions || {};
          setAvailableOptions(options);

          // 🎯 Pre-selección de segmentos: Todo menos DONACIONES EMPLEADOS y NO APLICA
          const allSegments = options.FACT_SEG_DES || [];
          const excluded = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
          const prefilteredSegments = allSegments.filter(seg => !excluded.includes(seg));

          setFilters(prev => ({
            ...prev,
            FACT_SEG_DES: prefilteredSegments,
            FACT_STATUS_ANULADA: [0 || '0']
          }));
          
          // ✅ Sincronización: Si el año actual no está en la data, usar el último año disponible
          if (conf.years && conf.years.length > 0 && !conf.years.includes(today.getFullYear())) {
            setSelectedYear([conf.years[0]]);
          }
          setSelectedDay([today.getDate()]); // ✅ Preseleccionar el día actual
        }
        
        filtersInitialized.current = true;
      } catch (e) {
        console.error('Config load error:', e);
      }
    };
    loadConfig();
  }, [getFactFilters, today]);

  // ✅ Único punto de ejecución: Reacciona a cambios de filtros o página de forma centralizada
  useEffect(() => {
    if (filtersInitialized.current) {
      const timer = setTimeout(async () => {
        const params = buildQueryParams(currentPage);
        await executeQuery(params);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedYear, selectedMonth, selectedDay, filters, currentPage, buildQueryParams, executeQuery]);

  // ✅ Resetear a página 1 cuando cambian los filtros de búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedMonth, selectedDay, filters]);

  const selectSuggestion = (sug) => {
    setFilters(prev => ({ ...prev, [sug.category]: [sug.value] }));
    setSelectedCategory(sug);
    setSearchValue('');
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  const handleRefresh = useCallback(async () => {
  try {
    setIsQuerying(true);

    // Recargar config SIN destruir UI
    const conf = await getFactFilters();
    let segmentosValidos = [];

    if (conf) {
      setConfig(conf);
      setDynamicYears(conf.years || []);
      const options = conf.availableOptions || {};
      setAvailableOptions(options);

      segmentosValidos = (options.FACT_SEG_DES || []).filter(
        seg => seg !== 'DONACIONES EMPLEADOS' && seg !== 'NO APLICA'
      );
    }

    // Reset filtros
    const resetFilters = {
      ...INITIAL_FILTERS,
      FACT_SEG_DES: segmentosValidos,
      FACT_STATUS_ANULADA: [0]
    };

    setFilters(resetFilters);
    setSearchValue('');
    setSelectedCategory(null);

    setSelectedYear([today.getFullYear()]);
    setSelectedMonth([nombreMesActual]);
    setSelectedDay([today.getDate()]); // ✅ Preseleccionar el día actual

    setCurrentPage(1);

    // Ejecutar query inmediatamente
    const params = {
      page_anios: [today.getFullYear()],
      page_meses: [nombreMesActual],
      page_dias: [today.getDate()], // ✅ Incluir el día actual en la query
      filters: resetFilters,
      page: 1,
      page_size: PAGE_SIZE
    };

    await executeQuery(params);

  } catch (e) {
    console.error('Refresh error:', e);
  } finally {
    setIsQuerying(false);
  }
}, [
  getFactFilters,
  executeQuery,
  today,
  nombreMesActual
]);

  const handleExportExcel = useCallback(async () => {
    if (!config) return;
    setIsQuerying(true);
    try {
      const params = {
        ...buildQueryParams(1),
        page_size: 50000 // Large enough for export
      };
      const result = await factQuery(params);

      const tableData =
        result?.data?.rows ||
        result?.data ||
        result?.table_fact?.rows ||
        result?.table_fact ||
        [];

      if (Array.isArray(tableData) && tableData.length > 0) {
        const data = tableData.map(row => ({
          Fecha: row.display_date || row.FACT_FECHA,
          Factura: row.FACT_FACTURA,
          Cliente: row.FACT_CLIENTE_NOMBRE,
          Producto: row.FACT_PRODUCTO,
          Región: row.FACT_REGION,
          Vendedor: row.FACT_VENDEDOR_ACTUAL,
          Marca: row.FACT_MARCA,
          Línea: row.FACT_LINEA,
          Proveedor: row.FACT_PROVEEDOR,
          Cantidad: Number(row.FACT_CANTIDAD || 0),
          'Descuento_%': Number(row.FACT_DESC || 0),
          'Monto_USD': Number(row.FACT_MONTO_USD || 0),
          Estado: row.FACT_STATUS_ANULADA === 1 ? 'ANULADA' : 'EFECTIVA'
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Facturacion");
        XLSX.writeFile(wb, `Detalle_Facturacion_${new Date().toISOString().split('T')[0]}.xlsx`);
      }
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setIsQuerying(false);
    }
  }, [config, buildQueryParams, factQuery]);

const handleDownloadFacturaPDF = async (group) => {
  try {
    const doc = new jsPDF({
  compress: true // Fuerza a jsPDF a usar algoritmos de compresión Flate
});
    const pageWidth = doc.internal.pageSize.getWidth();
    let currentY = 12;

    // Tasa BCV (Traerla de tu objeto group, si no, usar una por defecto para el cálculo)
    const tasaBCV = Number(group.tasaFactura || group.items[0]?.FACT_TASA1 || 540.0431); // Ejemplo según tu PDF

// =====================================================
// HEADER (Parte Superior Ajustada para evitar desborde)
// =====================================================
doc.setFont('helvetica', 'bold');
doc.setFontSize(24);
doc.setTextColor(0);

doc.addImage(
    dimassiLogo,
    'SVG',
    10,
    currentY + 1,
    50,
    0
);

// 2. Bloque de Dirección Fiscal arriba a la derecha (CENTRADOS ENTRE SÍ)
// Movemos el centro del bloque más a la izquierda (de 55 a 68) para dar suficiente margen derecho
const centroBloqueDerechoX = pageWidth - 68; 

doc.setFont('helvetica', 'bold');
doc.setFontSize(10);
doc.text('DIMASSI, C.A.', centroBloqueDerechoX, currentY + 2, { align: 'center' }); 

doc.setFont('helvetica', 'normal');
// Bajamos a 6.5 el tamaño de fuente para que las líneas largas respiren y no se salgan
doc.setFontSize(6.5); 
doc.setTextColor(60, 60, 60);

// Al estar el centro en 'pageWidth - 68', el texto se distribuye perfectamente sin tocar los bordes
doc.text('Domicilio Fiscal: Av. Principal de La Castellana, Calle Blandin, Edif. Torre Digitel, Piso 12. Of. B. Urb. La Castellana', centroBloqueDerechoX, currentY + 6, { align: 'center' });
doc.text('Caracas (Chacao) Miranda. Zona Postal 1060 - Teléfonos: +58 (0212) 750.34.60 / 750.34.61 / 750.34.62', centroBloqueDerechoX, currentY + 9, { align: 'center' });

// Los links quedan perfectamente centrados en la misma vertical
doc.text('www.dimassicorp.com         @dimassivzla', centroBloqueDerechoX, currentY + 12, { align: 'center' });

// Bajamos espacio proporcional para arrancar el bloque paralelo de datos
currentY += 24;

// =====================================================
// CUERPO DE DATOS (Alineación Asimétrica de la Imagen)
// =====================================================
doc.setFontSize(7.5);
doc.setTextColor(0);

const derechaEtiquetaX = pageWidth - 65; 
const derechaValorX = pageWidth - 12;     

// Inicializamos las alturas bases
let izqY = currentY;
let derY = currentY;

// --- FILA 1 DE LA DERECHA (Factura queda sola arriba) ---
doc.setFont('helvetica', 'normal');
doc.text('Factura:', derechaEtiquetaX, derY);
doc.setFont('helvetica', 'normal');
doc.text(`${group.factura || '107649'}`, derechaValorX, derY, { align: 'right' }); // 

// Avanzamos ambas variables una línea abajo. 
// De esta forma, la izquierda empezará un renglón después.
izqY += 4.5;
derY += 4.5; 

// --- FILA 2 DERECHA / FILA 1 IZQUIERDA (Cliente y Fecha Emisión alineados) ---
// Izquierda

doc.setFont('helvetica', 'normal');
doc.setFontSize(7.5);
doc.text('Cliente:', 12, izqY); doc.setFont('helvetica', 'normal'); 
const clienteId = String(group.clienteId || '0163').padStart(4, '0'); doc.text(clienteId, 26, izqY);
doc.setFontSize(7.5);
doc.setFont('helvetica', 'normal');
doc.text('Razon Social:', 44, izqY); 

// CÁLCULO DE LIMITE PERFECTO: Evita que invada 'derechaEtiquetaX'
// Si 'pageWidth' es 210 y 'derechaEtiquetaX' es (210 - 65) = 145, el espacio disponible desde X=65 es 80mm.
// Le restamos 4mm de margen de seguridad interno.
const espacioMaximoNombre = derechaEtiquetaX - 65 - 2;

doc.setFont('helvetica', 'normal'); // En Profit la razón social va destacada en negrita
doc.text(`${group.cliente || 'DIMASSI, C.A.'}`, 65, izqY, { maxWidth: espacioMaximoNombre });

doc.setFontSize(7.5);
// Derecha (A la misma altura exacta de izqY gracias al ajuste previo)
doc.setFont('helvetica', 'normal');
doc.text('Fecha Emisión:', derechaEtiquetaX, derY); // 
doc.text(`${group.fecha || '27/05/2026'}`, derechaValorX, derY, { align: 'right' }); // 


// --- PRÓXIMA FILA (R.I.F. / Fecha Vencim.) ---
izqY += 4.5;
derY += 4.5;

// Izquierda
doc.setFont('helvetica', 'normal');
doc.text('R.I.F.:', 12, izqY); // [cite: 3]
doc.setFont('helvetica', 'normal');
doc.text(`${group.rif || 'J408471906'}`, 26, izqY); // [cite: 5]

doc.setFont('helvetica', 'normal');
doc.text('Telf:', 82, izqY);

doc.setFont('helvetica', 'normal');
doc.text(
    `${group.FACT_TLF || group.items[0]?.FACT_TLF || ''}`,
    90,
    izqY
);

// Derecha
doc.setFont('helvetica', 'normal');
doc.text('Fecha Vencim.:', derechaEtiquetaX, derY); // 
const fechaVencRaw = group.FACT_VENCIMIENTO || group.items[0]?.FACT_VENCIMIENTO || '2026-06-27';
let fechaVencFormateada = fechaVencRaw;

// Si viene en formato AAAA-MM-DD, lo transformamos a DD/MM/AAAA
if (fechaVencRaw.includes('-')) {
  const [anio, mes, dia] = fechaVencRaw.split('T')[0].split('-'); // El .split('T')[0] limpia si viene con horas de base de datos
  fechaVencFormateada = `${dia}/${mes}/${anio}`;
}

doc.setFont('helvetica', 'normal');
doc.text(`${fechaVencFormateada}`, derechaValorX, derY, { align: 'right' });


// --- PRÓXIMA FILA (Dirección Fiscal / Condic. Pago) ---
izqY += 4.5;
derY += 4.5;

// Izquierda
doc.setFont('helvetica', 'normal');
doc.setFontSize(7.5);
doc.text('Dirección Fiscal:', 12, izqY); 

// Corrección aplicada: se extrae la dirección desde FACT_DIRECCION_FISCAL del primer ítem o del grupo
const direccionFiscalCliente = group.FACT_DIRECCION_FISCAL || group.items[0]?.FACT_DIRECCION_FISCAL || group.direccion || 'CARACAS';
doc.setFontSize(6.5);
doc.text(`${direccionFiscalCliente}`, 36, izqY, { maxWidth: derechaEtiquetaX - 38 });

doc.setFontSize(7.5);
// Derecha
doc.text('Condic. Pago:', derechaEtiquetaX, derY); // 
doc.setFont('helvetica', 'normal');
const condicion = group.FACT_CONPAGO || group.items[0]?.FACT_CONPAGO;
doc.text(`${condicion || 'CONTADO'}`, derechaValorX, derY, { align: 'right' }); // 


// --- PRÓXIMA FILA (Espacio vacío Izq / Vendedor Der) ---
izqY += 4.5; // Mantenemos la simetría de saltos en la izquierda aunque no pinte texto
derY += 4.5;

// Derecha
doc.setFont('helvetica', 'normal');
doc.text('Vendedor:', derechaEtiquetaX, derY);

const vendedorId = Number(
    group.FACT_ID_VEN || group.items[0]?.FACT_ID_VEN || 1
);

const vendedorFormateado =
    vendedorId < 100
        ? String(vendedorId).padStart(3, '0')
        : String(vendedorId);

doc.text(vendedorFormateado, derechaEtiquetaX + 16, derY);
 
doc.setFont('helvetica', 'normal');
doc.text('PK', derechaEtiquetaX + 26, derY); 
doc.setFont('helvetica', 'normal');
const pk = group.FACT_NOTENTREGA || group.items[0]?.FACT_NOTENTREGA;
doc.text(`${pk || '89291'}`, derechaValorX, derY, { align: 'right' }); 


// --- PRÓXIMA FILA (Dirección Entrega / O/C) ---
izqY += 4.5;
derY += 4.5;

// Izquierda
doc.setFont('helvetica', 'normal');
doc.setFontSize(7.5);
doc.text('Dirección Entrega:', 12, izqY); // [cite: 9]
const direccionEntregaCliente = group.FACT_DIRECCION_ENTREGA || group.items[0]?.FACT_DIRECCION_ENTREGA || group.direccionEntrega || 'CARACAS';
doc.setFontSize(6.5);
doc.text(`${direccionEntregaCliente}`, 36, izqY, { maxWidth: derechaEtiquetaX - 38 }); // [cite: 9]

doc.setFontSize(7.5);
// Derecha
doc.text('O/C:', derechaEtiquetaX, derY); // 
const ocompra = group.FACT_ORCOMPRA || group.items[0]?.FACT_ORCOMPRA;
doc.text(`${ocompra || 'Destruccion D-026-16'}`, derechaValorX, derY, { align: 'right' }); // 


// Sincronizamos currentY al punto más bajo de ambas columnas para continuar con la tabla
currentY = Math.max(izqY, derY) + 6;

// =====================================================
// CALCULAR TOTAL DE CANTIDADES
// =====================================================
const totalCantidadUnidades = group.items.reduce((acc, item) => {
  const unidadTipo = (item.FACT_UND || '').toUpperCase().trim();
  if (unidadTipo === 'UND') {
    return acc + Number(item.FACT_UNIDAD || item.FACT_CANTIDAD || 0);
  }
  return acc + Number(item.FACT_CANTIDAD || 0);
}, 0);

// =====================================================
// CAMPO "TOTAL:" (Acoplado de forma exacta al Header)
// =====================================================
doc.setFont('helvetica', 'normal');
doc.setFontSize(8);

const tablaFinX = pageWidth - 14; // Límite del borde derecho de la tabla
const cuadroTotalWidth = 24;      // Ancho idéntico a la columna Neto
const altoCuadro = 5;             // Alto del recuadro del total

// Dibujar la etiqueta "Total:"
doc.text('Total:', tablaFinX - cuadroTotalWidth - 4, currentY, { align: 'right' });

// Dibujar el recuadro del Total (Borde gris suave)
doc.setDrawColor(160, 160, 160);
doc.setLineWidth(0.1);
doc.rect(tablaFinX - cuadroTotalWidth, currentY - 3.5, cuadroTotalWidth, altoCuadro);

// Pintar el valor numérico
doc.setFont('helvetica', 'normal');
doc.text(`${totalCantidadUnidades}`, tablaFinX - 2, currentY, { align: 'right' });

// IMPORTANTE: El startY de la tabla debe comenzar exactamente donde termina el cuadro
// currentY - 3.5 es el tope del cuadro + altoCuadro(5) = currentY + 1.5 es la base exacta.
const tablaStartY = currentY - 3.5 + altoCuadro;

// =====================================================
// TABLA DE ITEMS (Bordes solo en Header, interna limpia)
// =====================================================
autoTable(doc, {
  startY: tablaStartY,             // Cero espacio con el total de arriba
  margin: { left: 14, right: 14 }, 
  
  head: [[
    'Código',
    'Descripción',
    'Unid.',
    'Cantidad',
    'Precio Unitario',
    '%Desc',
    'Neto'
  ]],

  body: group.items.map(item => {
    // 1. CONTROL DE CANTIDAD SEGÚN UNIDAD
    const unidadTipo = (item.FACT_UND || '').toUpperCase().trim();
    let cantidad = 1;
    
    if (unidadTipo === 'UND') {
      cantidad = Number(item.FACT_UNIDAD || item.FACT_CANTIDAD || 1);
    } else {
      cantidad = Number(item.FACT_CANTIDAD || 1);
    }

  // 2. CONTROL DE TASAS Y PRECIOS (Conversión a Bs. Asegurada)
    const monedaFactura = (group.FACT_MONEDA || group.moneda || group.items[0]?.FACT_MONEDA || 'USD').toUpperCase().trim();
    const tasaFactura = Number(item.FACT_TASA1 || group.tasaBCV || 1);
    
    let precioUnitarioRender = 0;
    let netoRender = 0;
    const descuentoPct = Number(item.FACT_DESC || 0);

    if (monedaFactura === 'BS' || monedaFactura === 'VES') {
      // --- CASO FACTURA EN BOLÍVARES (Bs.) ---
      // El backend ya nos trae el NETO en FACT_NETO, no lo volvemos a descontar.
      const precioBaseUSD = Number(item.FACT_PRECIO_UNIT || 0);
      precioUnitarioRender = precioBaseUSD * tasaFactura;

      if (precioUnitarioRender === 0 && cantidad > 0) {
        precioUnitarioRender = Number(item.FACT_MONTO || (item.FACT_MONTO_USD * tasaFactura)) / cantidad;
      }

      netoRender = Number(item.FACT_NETO || 0);

    } else {
      // --- CASO FACTURA EN DÓLARES (USD) ---
      precioUnitarioRender = Number(item.FACT_PRECIO_UNIT || 0);
      if (precioUnitarioRender === 0 && cantidad > 0) {
        precioUnitarioRender = Number(item.FACT_MONTO_USD || 0) / cantidad;
      }

      netoRender = Number(item.FACT_MONTO_USD || 0);
    }

    return [
      item.FACT_ART_ID || '',
      item.FACT_PRODUCTO || '',
      item.FACT_UND || 'UND',
      formatNum(cantidad),
      precioUnitarioRender.toFixed(2),
      `${descuentoPct.toFixed(2)}%`,
      netoRender.toFixed(2)
    ];
}),

  // Estilos base de la tabla: SIN BORDES por defecto
  styles: {
    font: 'helvetica',
    fontSize: 7,
    cellPadding: { top: 1, bottom: 1, left: 1, right: 1 },
    valign: 'middle',
    textColor: [40, 40, 40],
    lineWidth: 0,                 // Elimina líneas por defecto de las celdas
    borderColor: [255, 255, 255]  // Asegura que no se dibujen bordes internos
  },

  // Solo el encabezado tiene permitido llevar bordes (Estructura Profit)
  headStyles: {
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    fontStyle: 'normal',
    halign: 'center',
    lineWidth: 0.1,               // Borde delgado activo únicamente aquí
    borderColor: [160, 160, 160]  // Color de borde idéntico al del cuadro Total
  },

  // El cuerpo se mantiene totalmente plano y limpio
  bodyStyles: {
    fillColor: [255, 255, 255],
    lineWidth: 0                  // Forzar que no haya líneas divisorias entre productos
  },

  columnStyles: {
    0: { cellWidth: 26, halign: 'center' }, 
    1: { cellWidth: 72, halign: 'left' },   
    2: { cellWidth: 12, halign: 'center' }, 
    3: { cellWidth: 14, halign: 'right' },  
    4: { cellWidth: 22, halign: 'right' },  
    5: { cellWidth: 12, halign: 'right' },  
    6: { cellWidth: 24, halign: 'right' }   
  }
});

currentY = doc.lastAutoTable.finalY + 6;

// =====================================================
// SECCIÓN DE TOTALES MULTI-MONEDA (Distribución Fiel)
// =====================================================

// 1. Margen inmediato con el final de la tabla
currentY = doc.lastAutoTable.finalY + 1.5;

// Configuramos el grosor y el color para que aplique tanto a la línea como al cuadro
doc.setDrawColor(160, 160, 160);
doc.setLineWidth(0.1);

// 2. Línea divisoria (Divider) al 100% del ancho útil de la página
doc.line(14, currentY, pageWidth - 14, currentY);

const monedaFactura = (
    group.FACT_MONEDA ||
    group.moneda ||
    group.items[0]?.FACT_MONEDA ||
    'US$'
).toUpperCase();

// =====================================================
// VALIDACIÓN DE IMPUESTO
// FACT_IMP = 1 -> GRAVADO
// FACT_IMP = 6 -> EXENTO
// =====================================================

const factImp = group.FACT_IMP || group.items[0]?.FACT_IMP || 1;
const esExento = Number(factImp) === 6;

// =====================================================
// CONTROL GLOBAL LEFT Y
// =====================================================

let leftY = currentY + 4;

// =====================================================
// RECUADRO LEGAL USD
// =====================================================

if (monedaFactura === 'US$') {

    const anchoRecuadroBs = 110;
    const altoRecuadroBs = 12;

    doc.rect(14, currentY, anchoRecuadroBs, altoRecuadroBs);

    leftY = currentY + 4;

    doc.setFontSize(7);
    doc.setTextColor(0);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);

    doc.text(
        'A efecto de lo dispuesto en el artículo 25 de la Ley de Impuesto al Valor Agregado,\nse expresan los montos en Bolívares Bs., tomando como referencia\nla tasa del BCV a la hora de la emisión, Tasa de cambio:',
        17,
        leftY
    );

    doc.setFont('helvetica', 'bold');
    doc.text(`${tasaBCV.toFixed(4)}`, 83, leftY + 6.7);

    doc.setFont('helvetica', 'normal');
    doc.text('Bolívares por dólar.', 97, leftY + 6.7);

    leftY = currentY + altoRecuadroBs - 4;
}

// =====================================================
// FUNCIÓN NUMERO A LETRAS
// =====================================================

const numeroALetras = (num) => {

    const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];

    const decenas = [
        'DIEZ',
        'ONCE',
        'DOCE',
        'TRECE',
        'CATORCE',
        'QUINCE',
        'DIECISEIS',
        'DIECISIETE',
        'DIECIOCHO',
        'DIECINUEVE'
    ];

    const decenasDec = [
        '',
        '',
        'VEINTE',
        'TREINTA',
        'CUARENTA',
        'CINCUENTA',
        'SESENTA',
        'SETENTA',
        'OCHENTA',
        'NOVENTA'
    ];

    const centenas = [
        '',
        'CIENTO',
        'DOSCIENTOS',
        'TRESCIENTOS',
        'CUATROCIENTOS',
        'QUINCENTOS',
        'SEISCIENTOS',
        'SIETECIENTOS',
        'OCHOCIENTOS',
        'NOVECIENTOS'
    ];

    const filtrarCentenas = (n) => {

        if (n === 100) return 'CIEN';

        let c = Math.floor(n / 100);
        let d = Math.floor((n % 100) / 10);
        let u = n % 10;

        let texto = centenas[c];

        if (d === 1) {
            texto += ' ' + decenas[u];
        }
        else if (d > 1) {
            texto += ' ' + decenasDec[d] + (u > 0 ? ' Y ' + unidades[u] : '');
        }
        else if (u > 0) {
            texto += ' ' + unidades[u];
        }

        return texto.trim();
    };

    const procesarCompleto = (n) => {

        if (n === 0) return 'CERO';

        let entero = Math.floor(n);
        let textoEntero = '';

        if (entero >= 1000000) {

            let millones = Math.floor(entero / 1000000);

            textoEntero += (
                millones === 1
                    ? 'UN MILLON'
                    : filtrarCentenas(millones) + ' MILLONES'
            );

            entero %= 1000000;
        }

        if (entero >= 1000) {

            let miles = Math.floor(entero / 1000);

            textoEntero += ' ' + (
                miles === 1
                    ? 'MIL'
                    : filtrarCentenas(miles) + ' MIL'
            );

            entero %= 1000;
        }

        if (entero > 0) {
            textoEntero += ' ' + filtrarCentenas(entero);
        }

        return textoEntero.trim();
    };

    const centavos = Math.round((num % 1) * 100);
    const stringCentavos = centavos.toString().padStart(2, '0');

    return `${procesarCompleto(num)} CON ${stringCentavos}/100`;
};

// =====================================================
// POSICIONES
// =====================================================

let rightY = currentY + 4;

const totalX = pageWidth - 14;
const etiquetaX = pageWidth - 55;

// =====================================================
// FACTURA EN BOLÍVARES
// =====================================================

if (monedaFactura === 'BS' || monedaFactura === 'VES') {

    doc.setDrawColor(160, 160, 160);
    doc.setLineWidth(0.1);

    doc.line(14, currentY + 0.5, pageWidth - 14, currentY + 0.5);

    const porcDescCli =
        group.FACT_DESC_CLI ||
        group.items[0]?.FACT_DESC_CLI ||
        group.porc_desc ||
        0.00;

    // El neto ya viene sin el descuento, lo asignamos directo como el neto base
    const subTotalNetoBs =
        group.netoFinalBs ||
        (group.netoFinalUSD * tasaBCV) ||
        0.00;

    // Calculamos el descuento SOLO para mostrarlo de forma informativa
    // Si tu neto ya tiene el descuento aplicado, el "Subtotal Bruto" teórico sería subTotalNetoBs / (1 - desc)
    const factorOriginal = 1 - (porcDescCli / 100);
    const subTotalBrutoBs = porcDescCli > 0 && factorOriginal > 0 ? (subTotalNetoBs / factorOriginal) : subTotalNetoBs;
    const descBs = subTotalBrutoBs - subTotalNetoBs;

    // =================================================
    // LÓGICA EXENTO
    // =================================================

    const exentoBs = esExento ? subTotalNetoBs : 0;
    const baseImponibleBs = esExento ? 0 : subTotalNetoBs;
    const ivaBs = esExento ? 0 : baseImponibleBs * 0.16;
    const totalNetoBs = exentoBs + baseImponibleBs + ivaBs;

    // =================================================
    // BLOQUE DERECHO
    // =================================================

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);

    // EXENTO
    doc.text(
        'Exento Bs.:',
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        formatNum(Number(exentoBs.toFixed(2))),
        totalX,
        rightY,
        { align: 'right' }
    );

    // BASE IMPONIBLE
    rightY += 5;

    doc.text(
        'Sub-Total B.I. Bs.:',
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        formatNum(Number(subTotalNetoBs.toFixed(2))),
        totalX,
        rightY,
        { align: 'right' }
    );

    // DESCUENTO (INFORMATIVO - NO SE VUELVE A RESTAR)
    rightY += 5;

    doc.text(
        `% Desc.:  ${porcDescCli.toFixed(2)}  % Bs.:`,
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        formatNum(Number(descBs.toFixed(2))),
        totalX,
        rightY,
        { align: 'right' }
    );

    // SUBTOTAL
    rightY += 5;

    doc.text(
        'Sub-Total Bs.:',
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        formatNum(Number((esExento ? totalNetoBs : subTotalNetoBs).toFixed(2))),
        totalX,
        rightY,
        { align: 'right' }
    );

    // IVA
    rightY += 5;

    doc.text(
        `I.V.A.: 16.00 % Sobre: ${formatNum(Number(baseImponibleBs.toFixed(2)))}`,
        etiquetaX - 15,
        rightY,
        { align: 'right' }
    );

    doc.text(
        'Bs.:',
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        formatNum(Number(ivaBs.toFixed(2))),
        totalX,
        rightY,
        { align: 'right' }
    );

    // TOTAL
    rightY += 6;

    doc.setFont('helvetica', 'bold');

    doc.text(
        'Total Neto Bs:',
        etiquetaX,
        rightY
    );

    doc.text(
        formatNum(Number(totalNetoBs.toFixed(2))),
        totalX,
        rightY,
        { align: 'right' }
    );

    rightY += 0.9;

    doc.setDrawColor(160, 160, 160);
    doc.setLineWidth(0.1);

    doc.line(14, rightY, pageWidth - 14, rightY);

    rightY += -1.5;

    // =================================================
    // BLOQUE IZQUIERDO
    // =================================================

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);

    doc.text("Total: ", 14, leftY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);

    doc.text(`${totalCantidadUnidades}`, 21, leftY);

    doc.setFontSize(6);

    leftY += 7;

    doc.setFont('helvetica', 'normal');

    const montoEnLetrasBs = numeroALetras(totalNetoBs);

    doc.text(
        `SON: ${montoEnLetrasBs}`,
        14,
        leftY
    );
}

    else {

    // =================================================
    // FACTURA USD
    // =================================================

    leftY = currentY + 8.5;

    const porcDescCli =
        group.FACT_DESC_CLI ||
        group.items[0]?.FACT_DESC_CLI ||
        group.porc_desc ||
        0.00;

    // El neto ya viene sin el descuento de forma nativa
    const subTotalNetoUSD = group.netoFinalUSD || 0.00;

    // Calculamos el descuento SOLO para mostrarlo de forma informativa
    const factorOriginal = 1 - (porcDescCli / 100);
    const subTotalBrutoUSD = porcDescCli > 0 && factorOriginal > 0 ? (subTotalNetoUSD / factorOriginal) : subTotalNetoUSD;
    const descUSD = subTotalBrutoUSD - subTotalNetoUSD;

    // =================================================
    // LÓGICA EXENTO USD
    // =================================================

    const exentoUSD = esExento ? subTotalNetoUSD : 0;
    const baseImponibleUSD = esExento ? 0 : subTotalNetoUSD;
    const ivaUSD = esExento ? 0 : baseImponibleUSD * 0.16;
    const totalNetoUSD = exentoUSD + baseImponibleUSD + ivaUSD;

    // =================================================
    // CONVERSIÓN BS
    // =================================================

    const subTotalBs = esExento 
        ? exentoUSD * tasaBCV 
        : baseImponibleUSD * tasaBCV;

    // Convertimos el IVA real de USD a Bs.
    const ivaBs = ivaUSD * tasaBCV;

    // Convertimos el Total Neto real de USD a Bs.
    const totalBs = totalNetoUSD * tasaBCV;

    // =================================================
    // BLOQUE DERECHO USD
    // =================================================

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);

    // EXENTO
    doc.text(
        'Exento USD.:',
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        exentoUSD.toFixed(2),
        totalX,
        rightY,
        { align: 'right' }
    );

    // BASE IMPONIBLE
    rightY += 5;

    doc.text(
        'Sub-Total B.I. USD.:',
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        subTotalNetoUSD.toFixed(2),
        totalX,
        rightY,
        { align: 'right' }
    );

    // DESCUENTO (INFORMATIVO - NO SE VUELVE A RESTAR)
    rightY += 5;

    doc.text(
        `% Desc.:  ${porcDescCli.toFixed(2)}  % USD.:`,
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        descUSD.toFixed(2),
        totalX,
        rightY,
        { align: 'right' }
    );

    // SUBTOTAL
    rightY += 5;

    doc.text(
        'Sub-Total USD.:',
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        esExento ? exentoUSD.toFixed(2) : baseImponibleUSD.toFixed(2),
        totalX,
        rightY,
        { align: 'right' }
    );

    // IVA
    rightY += 5;

    doc.text(
        `I.V.A.: 16.00 % Sobre: ${baseImponibleUSD.toFixed(2)}`,
        etiquetaX - 15,
        rightY,
        { align: 'right' }
    );

    doc.text(
        'USD.:',
        etiquetaX,
        rightY,
        { align: 'right' }
    );

    doc.text(
        ivaUSD.toFixed(2),
        totalX,
        rightY,
        { align: 'right' }
    );

    // TOTAL
    rightY += 7;

    doc.text(
        'Total Neto USD:',
        etiquetaX,
        rightY
    );

    doc.text(
        totalNetoUSD.toFixed(2),
        totalX,
        rightY,
        { align: 'right' }
    );

    // =================================================
    // BLOQUE IZQUIERDO BS EQUIVALENTE
    // =================================================

    leftY += 7;

    doc.setFont('helvetica', 'normal');

    const montoEnLetrasUSD = numeroALetras(totalNetoUSD);

    doc.text(
        `SON: ${montoEnLetrasUSD}`,
        14,
        leftY
    );

    const longitudTextoUSD = doc.getTextWidth(`SON: ${montoEnLetrasUSD} `);

    doc.text(
        ' DOLARES AMERICANOS',
        14 + longitudTextoUSD,
        leftY
    );

    leftY += 4;

    doc.text('Sub-Total:', 14, leftY);

    doc.text(
        formatNum(Number(subTotalBs.toFixed(2))),
        55,
        leftY,
        { align: 'right' }
    );

    doc.text('I.V.A.:', 70, leftY);

    doc.text(
        formatNum(Number(ivaBs.toFixed(2))),
        95,
        leftY,
        { align: 'right' }
    );

    leftY += 4;

    doc.text(
        `Total: ${formatNum(Number(totalBs.toFixed(2)))} Bolivares`,
        14,
        leftY
    );

    leftY += 3.5;

    doc.setFontSize(6);

    doc.text(
        'equivalentes BCV a la fecha de emisión de la factura.',
        14,
        leftY
    );

    leftY += 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);

    doc.text(
        'MONTO ACORDADO DEL PAGO ES EXPRESADO EN MONEDA EXTRANJERA (DOLARES AMERICANOS)',
        17,
        leftY
    );
}

// =====================================================
// CIERRE GEOMÉTRICO
// =====================================================

currentY = Math.max(leftY, rightY) + 2;

doc.setDrawColor(160, 160, 160);
doc.setLineWidth(0.1);

doc.line(14, currentY, pageWidth - 14, currentY);

// =====================================================
// RECUADRO IGTF
// =====================================================

doc.setDrawColor(160, 160, 160);
doc.setLineWidth(0.1);

const porcDescCli_IGTF = group.FACT_DESC_CLI || group.items[0]?.FACT_DESC_CLI || group.porc_desc || 0.00;
      const subTotalUSD_IGTF = group.netoFinalUSD || 0.00;
      
      const subTotalNetoUSD_IGTF =
          subTotalUSD_IGTF * (1 - porcDescCli_IGTF / 100);

const ivaUSD_IGTF =
    esExento
        ? 0
        : subTotalNetoUSD_IGTF * 0.16;

const totalNetoUSD_IGTF =
    subTotalNetoUSD_IGTF + ivaUSD_IGTF;

const igtfUSD =
    totalNetoUSD_IGTF * 0.03;

const totalConIGTF_USD =
    totalNetoUSD_IGTF + igtfUSD;

const altoBloqueIGTF = 14;

const anchoUtil = pageWidth - 28;

const puntoCorteX =
    14 + (anchoUtil * 0.72);

// CONTENEDOR

doc.rect(14, currentY, anchoUtil, altoBloqueIGTF);

// DIVISIÓN

doc.line(
    puntoCorteX,
    currentY,
    puntoCorteX,
    currentY + altoBloqueIGTF
);

// TEXTO LEGAL

doc.setFont('helvetica', 'normal');
doc.setFontSize(5.5);
doc.setTextColor(40, 40, 40);

const anchoTextoLegal =
    (puntoCorteX - 14) - 4;

const textoIGTF =
    "EN EL CASO EN QUE LA SIGUIENTE FACTURA SEA PAGADA TOTAL O PARCIALMENTE EN DIVISAS AL MONTO NETO A PAGAR DEBERÁ INCLUIRSE EL TRES PORCIENTO (3%) POR CONCEPTO DE IMPUESTO A LAS GRANDES TRANSACCIONES FINANCIERAS (IGTF) QUE SERÁ ENTERADO POR DIMASSI, C.A. A LA ADMINISTRACIÓN TRIBUTARIA EN CALIDAD DE AGENTE DE PERCEPCIÓN S/G.O. N° 42.339 DE FECHA 17/03/2022.";

doc.text(
    textoIGTF,
    16,
    currentY + 3.2,
    {
        maxWidth: anchoTextoLegal,
        align: 'justify'
    }
);

// DERECHA

const xMaxDerecha = pageWidth - 14;

doc.setFont('helvetica', 'normal');
doc.setFontSize(6.5);
doc.setTextColor(0);

doc.text(
    '* I.G.T.F.    3.00 %:',
    puntoCorteX + 2,
    currentY + 4
);

doc.text(
    igtfUSD.toFixed(2),
    xMaxDerecha - 2,
    currentY + 4,
    { align: 'right' }
);

doc.setFontSize(5.5);
doc.setTextColor(60, 60, 60);

const anchoTextoReferencial =
    (xMaxDerecha - puntoCorteX) - 4;

const textoReferencial =
    "* MONTO REFERENCIAL. EL MONTO REAL SERÁ CALCULADO SOBRE EL NETO PAGADO.";

doc.text(
    textoReferencial,
    puntoCorteX + 2,
    currentY + 8.5,
    {
        maxWidth: anchoTextoReferencial,
        align: 'left'
    }
);

// FINAL

currentY += altoBloqueIGTF + 3;



  // =====================================================
    // PIE DE IMPRENTA FISCAL (SENIAT - COLORES Y ESTILOS ENFOCADOS)
    // =====================================================
    currentY += 2; // Separación desde el bloque IGTF

    // --- COLUMNA IZQUIERDA: Identificación de la Forma Libre ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(0); // Negro
    doc.text('FORMA LIBRE / N° DE CONTROL:', 14, currentY);

    // --- PROCESAMIENTO DEL NÚMERO DE CONTROL DINÁMICO (FACT_NCONTROL) ---
    // Mapeamos las variables reales de tu base de datos para el número de control
    const numControlCompleto = group.FACT_NCONTROL || 
                               group.items[0]?.FACT_NCONTROL || 
                               group.numControl || 
                               group.items[0]?.numControl || 
                               'Sin N° Control Registrado'; // Respaldo por si acaso

    // Dividimos el string usando el guion de la base de datos
    const partesControl = numControlCompleto.split('-');
    const antesGuion = partesControl[0] || '00';
    const despuesGuion = partesControl.slice(1).join('-'); // Evita romper si trae más de un guion

    doc.setFontSize(14); 
    doc.setFont('helvetica', 'bold');
    
    // 1. Pintamos la parte antes del guion en NEGRO junto con el carácter '-'
    doc.setTextColor(0, 0, 0); 
    doc.text(`${antesGuion}-`, 18, currentY + 6);

    // Calculamos el espacio exacto que ocupó el prefijo para encajar el texto rojo
    const anchoAntesGuion = doc.getTextWidth(`${antesGuion}-`);

    // 2. Pintamos la parte después del guion en ROJO NEGRITA
    doc.setTextColor(180, 0, 0); // Rojo Corporativo
    doc.text(despuesGuion, 18 + anchoAntesGuion, currentY + 6);


    // --- COLUMNA DERECHA: Datos Técnicos de la Imprenta Autorizada ---
    const xBloqueImprenta = 75; 
    doc.setFontSize(5.2); 
    doc.setTextColor(100, 100, 100); // Gris para texto secundario

    // Primera línea de la imprenta con "OLIVENCA, c.a." resaltado en negrita
    doc.setFont('helvetica', 'normal');
    doc.text('Impreso en Venezuela por: ', xBloqueImprenta, currentY + 1.5);
    
    const anchoPrefijo = doc.getTextWidth('Impreso en Venezuela por: ');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0); // Negro para destacar el nombre
    doc.text('OLIVENCA, c.a.', xBloqueImprenta + anchoPrefijo, currentY + 1.5);

    const anchoNombreImprenta = doc.getTextWidth('OLIVENCA, c.a.');
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(
      ' - Urbanización Industrial La Urbina, Calle 6, Edificio Olivenca, Caracas, Teléfono: 241.85.67', 
      xBloqueImprenta + anchoPrefijo + anchoNombreImprenta, 
      currentY + 1.5
    );

    // Resto de filas técnicas estándar
    doc.text(
      `RIF: J-00027224-7 - Autorizado por Providencia Num. SENIAT-11-00046 de Fecha 30 - 01 - 2008 - Región Capital - Contribuyentes Especiales`, 
      xBloqueImprenta, 
      currentY + 4
    );

    doc.text(
      `N° de Control: Desde el N° 00-00179351 Hasta el N° 00-00189350 - Impresas el 08-04-2026 - Cantidad de Formas: 10.000`, 
      xBloqueImprenta, 
      currentY + 6.5
    );


    // --- ETIQUETA DE CIERRE: ORIGINAL CLIENTE EN ROJO NEGRITA ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(180, 0, 0); // Rojo Puro
    doc.text('ORIGINAL CLIENTE', pageWidth / 2, currentY + 11, { align: 'center' });

    // Reestablecer color por defecto por si acaso
    doc.setTextColor(0);

    // Auditoría de entorno en desarrollo
    if (process.env.NODE_ENV === 'development') { 
      console.log('Total percibido con IGTF simulado:', totalConIGTF_USD); 
    }

    // Guardar el documento
    doc.save(`FACTURA_${group.factura || '107649'}.pdf`);

  } catch (error) {
    console.error('PDF error:', error);
  }
};

  const totalPages = useMemo(() => {
    if (globalTotals.totalPages) return Number(globalTotals.totalPages);
    return Math.ceil(totalCount / PAGE_SIZE) || 1;
  }, [totalCount, globalTotals.totalPages]);

  const tableHeaders = [
      'Fecha',
      'Factura',
      'Cliente',
      'Cant. UND',
      'Cant. BL',
      'Monto Bs.',
      'Monto USD',
      'Moneda',
      'Desc.',
      'Neto Bs',
      'Neto USD',
      'Status',
      'PDF'
    ];

  if (loading && !config) {
    return <FactLoader message="Cargando datos de facturación..." />;
  }

  if (error) {
    return (
      <div style={{padding: '40px', textAlign: 'center', color: '#ff6b6b'}}>
        <h3>Error de conexión</h3>
        <p>{error}</p>
        <button onClick={handleRefresh} className="refresh-button" style={{marginTop: '20px'}}>
          <RefreshCw size={20} /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <FacturacionMenuHeader />
      
      <div style={{ marginTop: '20px' }}>
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Detalle de Facturación</h1>
            <div className="title-underline"></div>
          </div>

          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} title="Reiniciar filtros">
              <RefreshCw size={16} className={loading ? "spin-anim" : ""}/>
            </button>
            <button className="refresh-button" onClick={handleExportExcel} title="Exportar a Excel" style={{ background: '#00ba52' }}>
              <Download size={16} />
            </button>
            
            {/* Nuevo contenedor para agrupar filtros de fecha y búsqueda general */}
            <div className="filters-and-search-group">
              <div className="date-filters-group">
                <MultiSelectFilter label="Día" name="day" icon={Calendar} options={days} selectedValues={selectedDay} onChange={(_, v) => setSelectedDay(v)} />
                <MultiSelectFilter label="Mes" name="month" icon={Calendar} options={meses} selectedValues={selectedMonth} onChange={(_, v) => setSelectedMonth(v)} />
                <MultiSelectFilter label="Año" name="year" icon={Calendar} options={isMobile ? [today.getFullYear()] : dynamicYears} selectedValues={selectedYear} onChange={(_, v) => setSelectedYear(v)} />
              </div>

              <div className="global-search-container" ref={searchRef}>
                <div className="global-search-input-wrapper">
                  <input 
                    type="text" 
                    placeholder="Buscar producto o cliente..." 
                    value={searchValue} 
                    onChange={(e) => setSearchValue(e.target.value)} 
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters(1)} 
                    className="global-search-input" 
                  />
                  <button className="global-search-apply-btn" onClick={() => handleApplyFilters(1)}>
                    <Search size={18} />
                  </button>
                </div>

                <AnimatePresence>
                  {showSuggestions && suggestions.length > 0 && (
                    <motion.div 
                   className="global-search-dropdown"
                      initial={{ opacity: 0, y: -5 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      exit={{ opacity: 0, y: -5 }}
                    >
                      <div className="suggestions-list">
                        {suggestions.map((opt, index) => (
                          <div 
                            key={`${opt.key}-${index}`}
                            className={`suggestion-item ${selectedCategory?.value === opt.value && selectedCategory?.key === opt.key ? 'selected' : ''}`}
                            onClick={() => selectSuggestion(opt)}
                          >
                            <div className="suggestion-category">{opt.catLabel || opt.label}:</div>
                            <div className="suggestion-value">
                              {highlightMatch(opt.value, searchValue)}
                              {filters[opt.category]?.includes(opt.value) && (
                                <span className="suggestion-active">[OK]</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {suggestions.length >= 20 && (
                        <div className="suggestions-footer">
                          Mostrando top 20 resultados...
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <button className="mobile-filter-btn" onClick={() => setShowFilters(true)} style={!isMobile ? { marginLeft: 'auto' } : {}}>
              <Filter size={16} /> <span>Filtros</span>
            </button>
          </div>
        </header>

        <div className={`filters-sidebar-container ${showFilters ? 'open' : ''}`}>
           <div className="filters-sidebar-header">
             <h3>Filtros Avanzados</h3>
             <button className="close-filters-btn" onClick={() => setShowFilters(false)}><X size={20}/></button>
           </div>
           <div className="filters-grid">
             {Object.keys(filters).map(key => (
               <MultiSelectFilter key={key} label={filterLabels[key]} name={key} icon={Tag} options={availableOptions[key]} selectedValues={filters[key]} onChange={(n, v) => setFilters(prev => ({...prev, [n]: v}))} />
             ))}
             <button className="filter-pill-button reset-filters" onClick={() => setFilters(INITIAL_FILTERS)}><FilterX size={12} /> Limpiar</button>
           </div>
        </div>

        {/* Tarjetas de Totales Globales (Filtrados) */}
        <div className="dashboard-indicators3">
          
          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <ListPlus size={22} color="white" />
              </div>
              <span className="indicator-label">Total Facturas (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatNum(globalTotals.total_facturas)}</span>
            </div>
          </div>
          </div>


          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <Boxes size={22} color="white" />
              </div>
              <span className="indicator-label">Total Unidades (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatNum(globalTotals.unidad)}</span>
            </div>
          </div>
          </div>    



          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <PackagePlus size={22} color="white" />
              </div>
              <span className="indicator-label">Total Bultos (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatNum(globalTotals.bultos)}</span>
            </div>
          </div>
          </div>    


          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <Banknote size={22} color="white" />
              </div>
              <span className="indicator-label">Total Bs. (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatBs(globalTotals.bs)}</span>
            </div>
          </div>
          </div>    
          

          <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper">
                <DollarSign size={22} color="white" />
              </div>
              <span className="indicator-label">Total USD (Filtro)</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{formatUSD(globalTotals.usd)}</span>
            </div>
          </div>
          </div>    
        </div>

        <div className="detail-table-container">
            <div className="table-header">
              <h3>Detalle de Facturación</h3>
              <div className="table-controls">
                <button className="export-table-btn" onClick={handleExportExcel} disabled={rows.length === 0}>
                  <Download size={18} />
                </button>

                <span>Total registros: {totalCount}</span>

                <div className="pagination-controls">
                  <button
                    onClick={() => handleApplyFilters(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isQuerying}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {currentPage} de {totalPages || 1}
                  </span>
                  <button
                    onClick={() => handleApplyFilters(currentPage + 1)}
                    disabled={currentPage >= (totalPages || 1) || isQuerying}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>

            {rows.length === 0 ? (
              <div style={{ padding: '50px', textAlign: 'center', opacity: 0.5 }}>
                No hay registros que coincidan.
              </div>
            ) : (
              <>
                <div className="responsive-table-wrapper" ref={tableContainerRef} style={{ maxHeight: '62vh', overflow: 'auto' }}>
                  <table className="detail-table">
                    <thead>
                      <tr>
                        {tableHeaders.map((header, index) => (
                          <th
                            key={index}
                            style={{
                              textAlign:
                                header.includes('Monto USD') ||
                                header.includes('Cant BL') ||
                                header.includes('Precio')
                                  ? 'right'
                                  : 'left'
                            }}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {groupedRows.map((group, index) => {
                        const isExpanded = expandedRows[group.factura];

                        return (
                          <React.Fragment key={index}>

                            {/* FILA PRINCIPAL */}
                            <tr
                              onClick={() => toggleFactura(group.factura)}
                              style={{
                                opacity: 1,
                              }}
                            >
                              <td>{group.fecha}</td>

                              <td>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}>
                                  {isExpanded ? (
                                    <ChevronDown size={16} />
                                  ) : (
                                    <ChevronRight size={16} />
                                  )}

                                  <span>{group.factura}</span>
                                </div>
                              </td>

                              <td>
                                {group.cliente}
                                <div style={{
                                  fontSize: 11,
                                  opacity: 0.7
                                }}>
                                  ID: {group.clienteId}
                                </div>
                              </td>

                              <td style={{ textAlign: 'right' }}>
                                {formatNum(group.totalCUND)}
                              </td>

                              <td style={{ textAlign: 'right' }}>
                                {formatNum(group.totalCantidad)}
                              </td>

                              <td style={{
                                textAlign: 'right',
                              }}>
                                {formatBs(group.totalBs)}
                              </td>

                              <td style={{
                                textAlign: 'right',
                              }}>
                                {formatUSD(group.totalUSD)}
                              </td>

                              <td>
                                {group.moneda}
                              </td>

                              <td>
                                {group.descuentoPct.toFixed(2) + '%'}
                              </td>

                              <td style={{
                                textAlign: 'right',
                              }}>
                                {formatBs(group.netoFinalBs)}
                              </td>

                              <td style={{
                                textAlign: 'right',
                              }}>
                                {formatUSD(group.netoFinalUSD)}
                              </td>

                              <td>
                                <span
                                  style={{
                                    padding: '4px 10px',
                                    borderRadius: 8,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    background: group.items.some(item => item.FACT_STATUS_ANULADA === 1)
                                      ? 'rgb(101, 0, 0)'
                                      : 'rgb(0, 64, 30)',
                                    color: group.items.some(item => item.FACT_STATUS_ANULADA === 1)
                                      ? '#ff4d4f'
                                      : '#00d26a',
                                    border: group.items.some(item => item.FACT_STATUS_ANULADA === 1)
                                      ? '1px solid rgba(255,77,79,0.35)'
                                      : '1px solid rgba(0,210,106,0.35)'
                                  }}
                                >
                                  {group.items.some(item => item.FACT_STATUS_ANULADA === 1)
                                    ? 'ANULADA'
                                    : 'VIGENTE'}
                                </span>
                              </td>

                              <td>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadFacturaPDF(group);
                                }}
                                style={{
                                  background: '#ff003c',
                                  border: 'none',
                                  borderRadius: 8,
                                  padding: '7px 10px',
                                  cursor: 'pointer',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: 12
                                }}
                              >
                                PDF
                              </button>
                            </td>

                            </tr>

                            {/* DETALLES */}
                              {isExpanded && group.items.map((item, subIndex) => {

                                const itemDesc = Number(item.FACT_DESC || 0);

                                const brutoBs = Number(item.FACT_NETO || 0);
                                const brutoUSD = Number(item.FACT_MONTO_USD || 0);

                                const descuentoBs = brutoBs * (itemDesc / 100);
                                const descuentoUSD = brutoUSD * (itemDesc / 100);

                                const netoBs = brutoBs - descuentoBs;
                                const netoUSD = brutoUSD - descuentoUSD;

                                return (
                                  <tr
                                    key={`${index}-${subIndex}`}
                                    style={{
                                      background: 'rgba(255,255,255,0.02)'
                                    }}
                                  >
                                    <td></td>

                                    <td>
                                      {item.FACT_ART_ID}
                                    </td>

                                    <td>
                                      {item.FACT_PRODUCTO}
                                    </td>

                                    <td style={{ textAlign: 'right' }}>
                                      {formatNum(item.FACT_UNIDAD)}
                                    </td>

                                    <td style={{ textAlign: 'right' }}>
                                      {formatNum(item.FACT_CANTIDAD)}
                                    </td>

                                    {/* BRUTO BS */}
                                    <td style={{ textAlign: 'right' }}>
                                      {formatBs(brutoBs)}
                                    </td>

                                    {/* BRUTO USD */}
                                    <td style={{ textAlign: 'right' }}>
                                      {formatUSD(brutoUSD)}
                                    </td>

                                    <td>
                                      {item.FACT_UND}
                                    </td>

                                    {/* DESCUENTO */}
                                    <td style={{ textAlign: 'right' }}>
                                      {itemDesc.toFixed(2)}%
                                    </td>

                                    {/* NETO BS */}
                                    <td style={{ textAlign: 'right' }}>
                                      {formatBs(netoBs)}
                                    </td>

                                    {/* NETO USD */}
                                    <td style={{ textAlign: 'right' }}>
                                      {formatUSD(netoUSD)}
                                    </td>

                                    {/* STATUS */}
                                    <td>
                                      <span
                                        style={{
                                          padding: '3px 8px',
                                          borderRadius: 6,
                                          fontSize: 11,
                                          fontWeight: 600,
                                          background: item.FACT_STATUS_ANULADA === 1
                                            ? 'rgba(255,0,0,0.18)'
                                            : 'rgba(0,255,120,0.18)',
                                          color: item.FACT_STATUS_ANULADA === 1
                                            ? '#ff4d4f'
                                            : '#00d26a',
                                          border: item.FACT_STATUS_ANULADA === 1
                                            ? '1px solid rgba(255,77,79,0.35)'
                                            : '1px solid rgba(0,210,106,0.35)'
                                        }}
                                      >
                                        {item.FACT_STATUS_ANULADA === 1
                                          ? 'ANULADA'
                                          : 'VIGENTE'}
                                      </span>
                                    </td>

                                  </tr>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="table-footer-pagination" style={{ display: 'flex', bottom: 0, justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '0 0 12px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-third)' }}>
                      <List size={16} />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-third)'}}><strong>{formatNum(totalCount)}</strong> registros</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '20px' }}>
                      <DollarSign size={16} color="#00ff88" />
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-third)' }}>Total Pág: <strong>{formatUSD(pageTotalUSD)}</strong></span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.6, color: 'var(--text-third)' }}>
                    <Database size={14} />
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-third)' }}>DimassiDB Core</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
    </motion.div>
  );
};

export default DetalleFact;